import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CreateSellSheet } from '@/components/sheet/CreateSellSheet'
import { fetchSells, deleteSell } from '@/services/products'
import type { Sell } from '@/types/products'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  AlertCircle,
  ShoppingBag,
  DollarSign,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
  TrendingUp,
  Trash2,
} from 'lucide-react'
import { API_BASE_URL } from '@/lib/axios'

type SortField =
  | 'created_at'
  | 'cell'
  | 'height'
  | 'width'
  | 'quantity'
  | 'price'
  | 'profit'
  | 'customer_given_price'
  | 'material'
type SortDirection = 'asc' | 'desc' | null

function LoadingSkeleton() {
  return (
    <div className='space-y-2'>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className='h-16 w-full' />
      ))}
    </div>
  )
}

export default function SellPage() {
  const { t, i18n } = useTranslation()
  const [sells, setSells] = useState<Sell[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')

  // Sort state
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Create sheet state
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedSell, setSelectedSell] = useState<Sell | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    loadSells()
  }, [])

  const loadSells = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await fetchSells()
      setSells(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sells')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortDirection(null)
        setSortField('created_at')
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className='ml-1 h-3 w-3' />
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className='ml-1 h-3 w-3' />
    }
    if (sortDirection === 'desc') {
      return <ArrowDown className='ml-1 h-3 w-3' />
    }
    return <ArrowUpDown className='ml-1 h-3 w-3' />
  }

  const filteredSells = useMemo(() => {
    let result = sells.filter((item) => {
      if (searchQuery === '') return true
      const searchLower = searchQuery.toLowerCase()
      return (
        item.material.name.toLowerCase().includes(searchLower) ||
        item.material.color.toLowerCase().includes(searchLower) ||
        item.material.type.toLowerCase().includes(searchLower) ||
        item.cell.toString().includes(searchLower) ||
        item.height.toString().includes(searchLower) ||
        item.width.toString().includes(searchLower) ||
        item.created_by.first_name.toLowerCase().includes(searchLower) ||
        item.created_by.last_name.toLowerCase().includes(searchLower)
      )
    })

    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: number | string
        let bValue: number | string

        switch (sortField) {
          case 'created_at':
            aValue = new Date(a.created_at).getTime()
            bValue = new Date(b.created_at).getTime()
            break
          case 'cell':
            aValue = a.cell
            bValue = b.cell
            break
          case 'height':
            aValue = a.height
            bValue = b.height
            break
          case 'width':
            aValue = a.width
            bValue = b.width
            break
          case 'quantity':
            aValue = a.quantity
            bValue = b.quantity
            break
          case 'price':
            aValue = a.price
            bValue = b.price
            break
          case 'profit':
            aValue = a.profit
            bValue = b.profit
            break
          case 'customer_given_price':
            aValue = a.customer_given_price
            bValue = b.customer_given_price
            break
          case 'material':
            aValue = a.material.name
            bValue = b.material.name
            break
          default:
            return 0
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
        return 0
      })
    }

    return result
  }, [sells, searchQuery, sortField, sortDirection])

  const clearFilters = () => {
    setSearchQuery('')
  }

  const hasActiveFilters = searchQuery !== ''

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'uz-UZ'

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    }).format(num)
  }

  const formatCurrency = (num: number) => {
    return '$' + formatNumber(num)
  }

  const totalSales = sells.length
  const totalRevenue = sells.reduce((sum, s) => sum + s.price * s.quantity, 0)
  const totalProfit = sells.reduce((sum, s) => sum + s.profit * s.quantity, 0)

  const handleOpenCreate = () => {
    setIsSheetOpen(true)
  }

  const handleSellSuccess = async () => {
    const data = await fetchSells()
    setSells(data)
  }

  const handleDeleteClick = (sell: Sell) => {
    setSelectedSell(sell)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteSell = async () => {
    if (!selectedSell) return
    try {
      setIsDeleting(true)
      await deleteSell(selectedSell.id)
      setSells((prev) => prev.filter((s) => s.id !== selectedSell.id))
      setIsDeleteDialogOpen(false)
      setSelectedSell(null)
    } catch (err) {
      console.error('Failed to delete sell:', err)
      alert('Failed to delete sell')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <header className='flex h-16 shrink-0 items-center justify-between gap-2 px-4'>
        <div className='flex items-center gap-2'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t('sell.title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <Button size='sm' onClick={handleOpenCreate}>
          <Plus className='mr-1 h-4 w-4' />
          {t('sell.addSale')}
        </Button>
      </header>

      {error && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error}</span>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => setError(null)}
            className='ml-auto'
          >
            {t('common.dismiss')}
          </Button>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        {/* Summary Cards */}
        <div className='grid gap-4 md:grid-cols-3'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('sell.totalSales')}
              </CardTitle>
              <ShoppingBag className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>
                {isLoading ? <Skeleton className='h-8 w-16' /> : totalSales}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('sell.totalRevenue')}
              </CardTitle>
              <DollarSign className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>
                {isLoading ? (
                  <Skeleton className='h-8 w-24' />
                ) : (
                  formatCurrency(totalRevenue)
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('sell.totalProfit')}
              </CardTitle>
              <TrendingUp className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
                {isLoading ? (
                  <Skeleton className='h-8 w-24' />
                ) : (
                  formatCurrency(totalProfit)
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Table Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('sell.salesHistory')}</CardTitle>
            <CardDescription>{t('sell.salesDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className='mb-4 flex flex-wrap items-center gap-3'>
              <div className='relative flex-1 min-w-50'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder={t('sell.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='pl-9'
                />
              </div>
              {hasActiveFilters && (
                <Button variant='ghost' size='sm' onClick={clearFilters}>
                  <X className='mr-1 h-4 w-4' />
                  {t('common.clear')}
                </Button>
              )}
            </div>

            {isLoading ? (
              <LoadingSkeleton />
            ) : filteredSells.length === 0 ? (
              <div className='py-8 text-center text-muted-foreground'>
                {sells.length === 0
                  ? t('sell.noSalesFound')
                  : t('sell.noSalesMatch')}
              </div>
            ) : (
              <div className='overflow-x-auto'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50'
                        onClick={() => handleSort('material')}
                      >
                        <div className='flex items-center'>
                          {t('sell.material')}
                          {getSortIcon('material')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50 text-right'
                        onClick={() => handleSort('cell')}
                      >
                        <div className='flex items-center justify-end'>
                          {t('sell.cell')}
                          {getSortIcon('cell')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50 text-right'
                        onClick={() => handleSort('height')}
                      >
                        <div className='flex items-center justify-end'>
                          {t('sell.height')}
                          {getSortIcon('height')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50 text-right'
                        onClick={() => handleSort('width')}
                      >
                        <div className='flex items-center justify-end'>
                          {t('sell.width')}
                          {getSortIcon('width')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50 text-right'
                        onClick={() => handleSort('quantity')}
                      >
                        <div className='flex items-center justify-end'>
                          {t('sell.quantity')}
                          {getSortIcon('quantity')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50 text-right'
                        onClick={() => handleSort('price')}
                      >
                        <div className='flex items-center justify-end'>
                          {t('sell.price')}
                          {getSortIcon('price')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50 text-right'
                        onClick={() => handleSort('profit')}
                      >
                        <div className='flex items-center justify-end'>
                          {t('sell.profit')}
                          {getSortIcon('profit')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50 text-right'
                        onClick={() => handleSort('customer_given_price')}
                      >
                        <div className='flex items-center justify-end'>
                          {t('sell.customerGivenPrice')}
                          {getSortIcon('customer_given_price')}
                        </div>
                      </TableHead>
                      <TableHead>{t('sell.createdBy')}</TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50'
                        onClick={() => handleSort('created_at')}
                      >
                        <div className='flex items-center'>
                          {t('sell.date')}
                          {getSortIcon('created_at')}
                        </div>
                      </TableHead>
                      <TableHead className='w-[50px]'></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSells.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className='flex flex-col'>
                            <span className='font-medium'>
                              {item.material.name}
                            </span>
                            <span className='text-xs text-muted-foreground'>
                              {item.material.type} • {item.material.color}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className='text-right'>
                          {formatNumber(item.cell)}
                        </TableCell>
                        <TableCell className='text-right'>
                          {formatNumber(item.height)} m
                        </TableCell>
                        <TableCell className='text-right'>
                          {formatNumber(item.width)} m
                        </TableCell>
                        <TableCell className='text-right font-medium'>
                          {formatNumber(item.quantity)}
                        </TableCell>
                        <TableCell className='text-right font-medium'>
                          {formatCurrency(item.price)}
                        </TableCell>
                        <TableCell className='text-right font-medium text-green-600 dark:text-green-400'>
                          {formatCurrency(item.profit)}
                        </TableCell>
                        <TableCell className='text-right font-medium'>
                          {formatCurrency(item.customer_given_price)}
                        </TableCell>
                        <TableCell>
                          <div className='flex items-center gap-2'>
                            <Avatar className='h-6 w-6'>
                              <AvatarImage
                                src={
                                  item.created_by.avatar
                                    ? `${API_BASE_URL}${item.created_by.avatar}`
                                    : undefined
                                }
                                alt={item.created_by.first_name}
                              />
                              <AvatarFallback className='text-xs'>
                                {item.created_by.first_name[0]}
                                {item.created_by.last_name[0]}
                              </AvatarFallback>
                            </Avatar>
                            <span className='text-sm'>
                              {item.created_by.first_name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className='text-sm text-muted-foreground'>
                          {formatDate(item.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant='ghost'
                            size='icon'
                            onClick={() => handleDeleteClick(item)}
                            className='h-8 w-8 text-muted-foreground hover:text-destructive'
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Results count */}
            {!isLoading && sells.length > 0 && (
              <div className='mt-4 text-sm text-muted-foreground'>
                {t('common.showing')} {filteredSells.length} {t('common.of')}{' '}
                {sells.length}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Sale Sheet */}
      <CreateSellSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        onSuccess={handleSellSuccess}
        showCustomerPicker
        showSellingTypePicker
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('sell.deleteConfirmation', {
                material: selectedSell?.material.name,
                cell: selectedSell?.cell,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSell}
              disabled={isDeleting}
              className='bg-destructive hover:bg-destructive/90'
            >
              {isDeleting ? t('common.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
